import {
    TEMPLATE_VARIABLE_ALIASES,
    TEMPLATE_VARIABLE_CANONICAL_VALUE_ONLY_ALIASES,
} from '@apps/web/lib/generated_template_variable_aliases';
import { type components } from '@libraries/typescript/api_client';

export type TemplateVariableFlow =
    components['schemas']['TemplateVariableFlow'];
export type TemplateVariableManifest =
    components['schemas']['TemplateVariableManifestRead'];
type OutreachType = components['schemas']['OutreachType'];

export type TemplateVariableOption = {
    readonly token: string;
    readonly aliases?: ReadonlyArray<string>;
    readonly label: string;
    readonly description?: string;
};

export type OutreachTemplateVariableGroups = {
    readonly smsSingle: Array<TemplateVariableOption>;
    readonly smsMultiShiftHeader: Array<TemplateVariableOption>;
    readonly smsConsolidatedShift: Array<TemplateVariableOption>;
    readonly smsGrouped: Array<TemplateVariableOption>;
    readonly smsSingleClientGrouped: Array<TemplateVariableOption>;
    readonly smsGroupHeader: Array<TemplateVariableOption>;
    readonly smsMultiItem: Array<TemplateVariableOption>;
    readonly voiceSingle: Array<TemplateVariableOption>;
    readonly voiceMulti: Array<TemplateVariableOption>;
};

type OutreachTemplateVariableFlowNames = {
    readonly [
        Group in keyof OutreachTemplateVariableGroups
    ]?: TemplateVariableFlow;
};

const OUTREACH_TEMPLATE_VARIABLE_FLOWS: Partial<
    Record<OutreachType, OutreachTemplateVariableFlowNames>
> = {
    callout: {
        smsSingle: 'callout_sms_single_shift',
        smsMultiShiftHeader: 'callout_sms_multi_shift_header',
        smsConsolidatedShift: 'callout_sms_consolidated_shift',
        smsGrouped: 'callout_sms_single_client_grouped',
        smsSingleClientGrouped: 'callout_sms_single_client_grouped',
        smsGroupHeader: 'callout_sms_group_header',
        smsMultiItem: 'callout_sms_multi_shift_item',
        voiceSingle: 'callout_voice_single_shift_opening_line',
        voiceMulti: 'callout_voice_multi_shift_opening_line',
    },
    recurring_shift: {
        smsSingle: 'recurring_sms_single_shift',
        smsMultiShiftHeader: 'recurring_sms_multi_client',
        smsConsolidatedShift: 'recurring_sms_consolidated_shift',
        smsGrouped: 'recurring_sms_multi_client',
        smsSingleClientGrouped: 'recurring_sms_single_client_grouped',
        smsGroupHeader: 'recurring_sms_client_group',
        smsMultiItem: 'recurring_sms_item',
        voiceSingle: 'recurring_voice_single_shift_opening_line',
        voiceMulti: 'recurring_voice_multi_shift_opening_line',
    },
    clock_in: {
        smsSingle: 'clock_in_sms',
        voiceSingle: 'clock_reminder_voice_opening_line',
    },
    clock_out: {
        smsSingle: 'clock_out_sms',
        voiceSingle: 'clock_reminder_voice_opening_line',
    },
};

function humanizeVariableName(name: string): string {
    return name.split('_').join(' ');
}

export function getTemplateVariablesForFlow(
    manifest: TemplateVariableManifest | undefined,
    flow: TemplateVariableFlow,
): Array<TemplateVariableOption> {
    const flowManifest = manifest?.flows.find((item) => item.flow === flow);
    return (
        flowManifest?.variables.map((variable) => ({
            token: variable.name,
            aliases: variable.aliases,
            label: humanizeVariableName(variable.name),
            description: variable.description,
        })) ?? []
    );
}

export function getTemplateVariableNames(
    variables: ReadonlyArray<TemplateVariableOption>,
): Array<string> {
    return variables.map((variable) => variable.token);
}

export function withTemplateVariableAliases(
    values: Readonly<Record<string, string>>,
): Record<string, string> {
    const expanded = { ...values };

    for (const [alias, canonical] of Object.entries(
        TEMPLATE_VARIABLE_ALIASES,
    )) {
        if (
            !TEMPLATE_VARIABLE_CANONICAL_VALUE_ONLY_ALIASES.has(alias) &&
            !Object.prototype.hasOwnProperty.call(expanded, canonical) &&
            Object.prototype.hasOwnProperty.call(expanded, alias)
        ) {
            expanded[canonical] = expanded[alias]!;
        }
    }

    for (const [alias, canonical] of Object.entries(
        TEMPLATE_VARIABLE_ALIASES,
    )) {
        if (Object.prototype.hasOwnProperty.call(expanded, canonical)) {
            if (
                TEMPLATE_VARIABLE_CANONICAL_VALUE_ONLY_ALIASES.has(alias) ||
                !Object.prototype.hasOwnProperty.call(expanded, alias)
            ) {
                expanded[alias] = expanded[canonical]!;
            }
        } else if (TEMPLATE_VARIABLE_CANONICAL_VALUE_ONLY_ALIASES.has(alias)) {
            delete expanded[alias];
        }
    }

    return expanded;
}

export function getTemplatePreviewValuesForVariables(
    variables: ReadonlyArray<TemplateVariableOption>,
    values: Readonly<Record<string, string>>,
): Record<string, string> {
    const expandedValues = withTemplateVariableAliases(values);
    const previewValues: Record<string, string> = {};

    for (const variable of variables) {
        for (const token of [variable.token, ...(variable.aliases ?? [])]) {
            const value = expandedValues[token];
            if (value !== undefined) {
                previewValues[token] = value;
            }
        }
    }

    return previewValues;
}

export function mergeTemplateVariables(
    ...variableGroups: ReadonlyArray<ReadonlyArray<TemplateVariableOption>>
): Array<TemplateVariableOption> {
    return [
        ...new Map(
            variableGroups.flat().map((variable) => [variable.token, variable]),
        ).values(),
    ];
}

export function organizationSupportsShiftRecurrence(
    organization:
        | {
              readonly general_settings?: {
                  readonly primary_data_source?: string | null;
              } | null;
          }
        | undefined,
): boolean {
    return organization?.general_settings?.primary_data_source === 'wellsky';
}

export function getOutreachTemplateVariableGroups(
    manifest: TemplateVariableManifest | undefined,
    outreachType: OutreachType,
): OutreachTemplateVariableGroups {
    const flowNames = OUTREACH_TEMPLATE_VARIABLE_FLOWS[outreachType];
    const variablesForGroup = (
        group: keyof OutreachTemplateVariableGroups,
    ): Array<TemplateVariableOption> => {
        const flow = flowNames?.[group];
        return flow ? getTemplateVariablesForFlow(manifest, flow) : [];
    };

    return {
        smsSingle: variablesForGroup('smsSingle'),
        smsMultiShiftHeader: variablesForGroup('smsMultiShiftHeader'),
        smsConsolidatedShift: variablesForGroup('smsConsolidatedShift'),
        smsGrouped: variablesForGroup('smsGrouped'),
        smsSingleClientGrouped: variablesForGroup('smsSingleClientGrouped'),
        smsGroupHeader: variablesForGroup('smsGroupHeader'),
        smsMultiItem: variablesForGroup('smsMultiItem'),
        voiceSingle: variablesForGroup('voiceSingle'),
        voiceMulti: variablesForGroup('voiceMulti'),
    };
}

export function getShiftConfirmationTemplateVariableGroups(
    manifest: TemplateVariableManifest | undefined,
) {
    const message = getTemplateVariablesForFlow(
        manifest,
        'shift_confirmation_sms',
    );
    const item = getTemplateVariablesForFlow(
        manifest,
        'shift_confirmation_sms_item',
    );
    return {
        message,
        item,
        all: mergeTemplateVariables(message, item),
    };
}
